/**
 * 로그인 상태.
 *
 * 토큰 주체가 두 가지다:
 *   `pending:<sub>`  구글 로그인은 했지만 아직 userId를 안 정함 → 가입 화면으로
 *   실제 userId      프로필이 있는 사용자 → 앱을 쓸 수 있음
 *
 * 서버가 로그인 응답의 `user`로 이 분기를 알려준다(null이면 신규).
 * 클라이언트가 토큰을 뜯어보지 않는 이유: 토큰 형식이 바뀌면 같이 깨진다.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import type { LoginResponse, ProfileResponse } from '@lifetimeline/shared';

import { api, clearToken, isAuthError, readToken, storeToken } from '../../lib/apiClient.js';
import { disableGoogleAutoSelect } from './googleSignIn.js';

export type AuthStatus =
  /** 저장된 토큰을 확인하는 중 */
  | 'loading'
  /** 로그인 안 함 */
  | 'anonymous'
  /** 구글 로그인은 했지만 프로필이 없음 → 가입 필요 */
  | 'pending'
  /** 프로필까지 있는 사용자 */
  | 'authenticated';

interface AuthValue {
  status: AuthStatus;
  profile: ProfileResponse | null;
  /** 구글 ID 토큰(또는 개발용 토큰)으로 로그인한다 */
  signIn: (idToken: string) => Promise<LoginResponse>;
  signOut: () => void;
  /** 가입·설정 변경 후 화면에 반영한다 */
  setProfile: (profile: ProfileResponse) => void;
  /** 가입이 끝나면 토큰이 새로 발급된다 */
  acceptToken: (token: string) => void;
}

const AuthContext = createContext<AuthValue | null>(null);

/**
 * 마지막으로 확인된 내 프로필.
 *
 * **헤더를 첫 프레임부터 제대로 그리려고** 둔다. 이게 없으면 `/api/me`가
 * 올 때까지 이름도 사진도 없는 동그라미가 떠 있다가 사진으로 바뀐다 —
 * 다크 모드에서는 잉크색이 거의 흰색이라 흰 원이 번쩍인다.
 *
 * ⚠️ 이건 **그림용 값이지 권한이 아니다.** 상태는 그대로 `loading`에
 * 머물고, 로그인이 필요한 화면은 서버 확인을 기다린다. 여기 있는 값으로
 * 무엇을 보여줄지 정하면, 토큰이 죽은 사람에게 남의 이름이 잠깐 보인다.
 */
const PROFILE_CACHE_KEY = 'lifetimeline.profile';

function readCachedProfile(): ProfileResponse | null {
  try {
    const raw = localStorage.getItem(PROFILE_CACHE_KEY);
    if (raw === null) {
      return null;
    }
    const parsed: unknown = JSON.parse(raw);
    // 저장해 둔 것이 옛 형식일 수 있다 — 최소한의 모양만 보고 아니면 버린다
    return typeof parsed === 'object' &&
      parsed !== null &&
      typeof (parsed as ProfileResponse).handle === 'string'
      ? (parsed as ProfileResponse)
      : null;
  } catch {
    // 시크릿 창이거나 저장소가 막힌 경우
    return null;
  }
}

function writeCachedProfile(profile: ProfileResponse | null): void {
  try {
    if (profile === null) {
      localStorage.removeItem(PROFILE_CACHE_KEY);
    } else {
      localStorage.setItem(PROFILE_CACHE_KEY, JSON.stringify(profile));
    }
  } catch {
    // 못 저장해도 이번 세션은 멀쩡하다 — 다음 새로고침에 한 번 번쩍일 뿐이다
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  /*
   * 토큰이 있는지는 **그릴 때 이미 안다**(localStorage는 동기다).
   *
   * 예전에는 무조건 `loading`으로 시작해 effect에서 `anonymous`로 내렸다.
   * 그러면 로그인한 적 없는 사람도 첫 프레임은 `loading`이라, 그 사이
   * 화면이 한 번 다른 모습으로 그려졌다가 바뀐다. 알 수 있는 것을 굳이
   * 한 프레임 미룰 이유가 없다.
   *
   * `loading`은 이제 **토큰이 있어서 확인 중일 때만** 나온다.
   */
  const [status, setStatus] = useState<AuthStatus>(() =>
    readToken() === null ? 'anonymous' : 'loading',
  );
  /*
   * 토큰이 있으면 **지난번에 본 내 프로필**로 시작한다. 서버 확인이 끝나면
   * 진짜 값으로 덮인다. 토큰이 없으면 남의 화면에 옛 이름이 남지 않게 버린다.
   */
  const [profile, setProfileState] = useState<ProfileResponse | null>(() =>
    readToken() === null ? null : readCachedProfile(),
  );

  // 저장된 토큰이 아직 살아 있는지 확인한다.
  // 만료된 토큰으로 로그인 상태처럼 보이면 모든 동작이 401로 실패한다.
  useEffect(() => {
    let cancelled = false;

    if (readToken() === null) {
      return;
    }

    api
      .get<ProfileResponse>('/api/me')
      .then((loaded) => {
        if (!cancelled) {
          setProfileState(loaded);
          writeCachedProfile(loaded);
          setStatus('authenticated');
        }
      })
      .catch((error: unknown) => {
        if (cancelled) {
          return;
        }
        // 프로필이 없는 토큰(pending)은 /api/me가 401을 준다.
        // 토큰 자체가 만료·위조된 경우와 구분할 수 없으므로 둘 다 비로그인으로 둔다 —
        // 가입이 필요한 사람은 다시 로그인하면 `user: null`로 분기된다.
        if (isAuthError(error)) {
          clearToken();
        }
        // 토큰이 죽었으면 그려 두었던 얼굴도 같이 지운다
        setProfileState(null);
        writeCachedProfile(null);
        setStatus('anonymous');
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const signIn = useCallback(async (idToken: string) => {
    const response = await api.post<LoginResponse>(
      '/api/auth/google',
      { idToken },
      { anonymous: true },
    );

    storeToken(response.token);
    setProfileState(response.user);
    writeCachedProfile(response.user);
    setStatus(response.user === null ? 'pending' : 'authenticated');
    return response;
  }, []);

  const signOut = useCallback(() => {
    // 구글 자동 로그인을 끈다. 안 그러면 로그아웃 직후 화면을 열자마자
    // 다시 로그인되어 "로그아웃이 안 된다"고 느낀다.
    disableGoogleAutoSelect();
    clearToken();
    setProfileState(null);
    writeCachedProfile(null);
    setStatus('anonymous');
  }, []);

  const setProfile = useCallback((next: ProfileResponse) => {
    setProfileState(next);
    writeCachedProfile(next);
    setStatus('authenticated');
  }, []);

  const acceptToken = useCallback((token: string) => {
    storeToken(token);
  }, []);

  const value = useMemo<AuthValue>(
    () => ({ status, profile, signIn, signOut, setProfile, acceptToken }),
    [status, profile, signIn, signOut, setProfile, acceptToken],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const value = useContext(AuthContext);
  if (value === null) {
    throw new Error('AuthProvider 안에서만 쓸 수 있습니다');
  }
  return value;
}
